import React from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Spin,
  Typography,
  Space,
  Tabs,
  Tag,
  Popconfirm,
  Tooltip,
  Progress,
  Card,
  Row,
  Col,
} from 'antd';
import {
  TeamOutlined,
  PlayCircleOutlined,
  FileTextOutlined,
  ArrowLeftOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import CreateCharacterModal from '../components/CreateCharacterModal';
import { 
  MethodSelectionModal,
  AIDescriptionModal,
  CreateStateModal
} from '../components/CreateCharacterStateModals';
import CreateScenarioImageModal from './NodeDetail/CreateScenarioImageModal';
import ScenarioProposalsModal from './NodeDetail/ScenarioProposalsModal';
import SampleScenarioModal from './NodeDetail/SampleScenarioModal';
import ScenarioFormModal from './NodeDetail/ScenarioFormModal';
import ScenarioImagePreviewModal from './NodeDetail/ScenarioImagePreviewModal';
import CharacterPlanModal from './NodeDetail/CharacterPlanModal';
import { PageTitleSetter } from '../components/PageTitleContext';
import NodeBasicInfoTab from './NodeDetail/NodeBasicInfoTab';
import NodeCharactersTab from './NodeDetail/NodeCharactersTab';
import NodeScenariosTab from './NodeDetail/NodeScenariosTab';
import { useNodeDetail } from './NodeDetail/useNodeDetail';

const { Title, Text } = Typography;

const NodeDetail: React.FC = () => {
  const { lifeTreeId, nodeId } = useParams<{ lifeTreeId: string; nodeId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || '1';

  const handleTabChangeInUrl = (key: string) => {
    setSearchParams({ tab: key }, { replace: true });
  };

  const {
    form,
    createScenarioForm,
    sampleScenarioForm,
    editScenarioForm,
    nodeDetail,
    lifetreeDetail,
    characterStatesData,
    loading,
    refreshing,
    saving,
    hasChanges,
    hasStateChanges,
    activatingNode,
    activatingStates,
    planningCharacters,
    deployingCharacters,
    resettingCharacters,
    deployingFull,
    deployingScenarios,
    autoDeploying,
    autoDeploymentProgress,
    deploymentProgress,
    deploymentScenarioProgress,
    planModalOpen,
    editablePlanCharacters,
    creatingCharactersFromPlan,
    creatingStatesFromPlan,
    deployingRelationships,
    deployingImages,
    formalizingCharacters,
    showCreateCharacterModal,
    creatingCharacter,
    creatingState,
    methodSelectionModalOpen,
    createNewStateModalOpen,
    aiDescriptionModalOpen,
    stateInitialData,
    showCreateScenarioModal,
    showSampleScenarioModal,
    creatingScenario,
    generatingSample,
    sampleScenarioData,
    showScenarioImagePreviewModal,
    selectedScenarioForImage,
    generatedScenarioImage,
    creatingScenarioImage,
    approvingScenarioImage,
    showEditScenarioModal,
    updatingScenario,
    activatingScenarioIds,
    quickGenScenarioIds,
    npcRows,
    showCreateScenarioImageModal,
    generatingScenarioPrompt,
    proposingScenarios,
    generatingSummary,
    scenarioProposals,
    showScenarioProposalsModal,
    processingProposalIds,
    createdProposalIds,
    nodeImages,
    aiConcept,
    isGeneratingConcept,
    isGeneratingNodeImage,
    setPlanModalOpen,
    setShowCreateCharacterModal,
    setMethodSelectionModalOpen,
    setSelectedCharacterForState,
    setShowCreateScenarioModal,
    setSampleScenarioData,
    setShowSampleScenarioModal,
    setShowScenarioImagePreviewModal,
    setGeneratedScenarioImage,
    setSelectedScenarioForImage,
    setShowEditScenarioModal,
    setEditingScenario,
    setShowScenarioProposalsModal,
    setAIDescriptionModalOpen,
    setCreateNewStateModalOpen,
    setStateInitialData,
    setShowCreateScenarioImageModal,
    handlePlanCharacters,
    handleFormalizeCharacters,
    updatePlanCharacter,
    handleCreateCharactersFromPlan,
    handleCreateStatesFromPlan,
    handleDeployRelationships,
    handleDeployImages,
    handleDeployCharacters,
    handleResetCharacters,
    handleDeployScenarios,
    handleAutoDeployNode,
    handleSave,
    handleSetCoverImage,
    handleActivateNode,
    handleActivateState,
    handleCreateCharacter,
    handleMethodSelection,
    handleAICompletion,
    handleCreateNewState,
    handleAddCharacterState,
    handleRemoveCharacterState,
    handleProposeScenarios,
    handleGenerateSummary,
    handleSelectScenarioProposal,
    handleCreateScenario,
    handleGenerateSampleScenario,
    handleUpdateScenario,
    handleActivateScenario,
    handleQuickGenScenarioImage,
    handleDeleteScenario,
    handleApproveScenarioImage,
    handleGenerateNodeImageConcept,
    handleGenerateNodeImage,
    handleNPCCharacterChange,
    handleNPCStateChange,
    handleRemoveNPCRow,
    handleGenerateScenarioPrompt,
    handleCreateScenarioImageWithPrompt,
    handleFormChange,
    getAvailableNPCsForScenario,
    getAvailableNPCCharacters,
    getAvailableStatesForCharacter,
    getCharacterNameById,
    getCharacterStatesForAI,
    getSelectedCharacterName,
    openEditScenarioModal,
    openCreateScenarioImageModal,
  } = useNodeDetail(lifeTreeId, nodeId);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!nodeDetail || !lifetreeDetail) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Title level={3}>Node không tìm thấy</Title>
        <Button onClick={() => navigate(-1)}>Quay lại</Button>
      </div>
    );
  }

  const mainCharacter = lifetreeDetail.characters.find(char => char.type === 'main') || null;
  const mainCharacterStateInNode = characterStatesData.find(row => row.characterType === 'main') || null;

  const isNodeActive = nodeDetail.status === 'active';
  const hasImage = !!nodeDetail.imageUrl;
  const draftStates = nodeDetail.characterStates.filter(cs => cs.status !== 'active');
  const allStatesActive = draftStates.length === 0;
  const hasActiveScenario = nodeDetail.scenarios.some(sc => sc.status === 'active');
  const hasSummary = !!(nodeDetail.summary && nodeDetail.summary.trim());
  const isFirstNode = nodeDetail.level === 1;
  const hasRequiredExp = isFirstNode || nodeDetail.requiredExp > 0;
  const canActivate = hasImage && allStatesActive && hasActiveScenario && hasSummary && hasRequiredExp;

  const activateTooltipContent = (
    <div style={{ minWidth: 220 }}>
      <div style={{ marginBottom: 6, fontWeight: 600 }}>Điều kiện kích hoạt node:</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        {hasImage
          ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
          : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
        <span>Node có ảnh bìa</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 4 }}>
        {allStatesActive
          ? <CheckCircleOutlined style={{ color: '#52c41a', marginTop: 2 }} />
          : <CloseCircleOutlined style={{ color: '#ff4d4f', marginTop: 2 }} />}
        <span>
          Tất cả state đã kích hoạt
          {!allStatesActive && (
            <span style={{ color: '#faad14', display: 'block', fontSize: '12px' }}>
              Còn {draftStates.length} state chưa active:{' '}
              {draftStates.map(s => s.name).join(', ')}
            </span>
          )}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        {hasActiveScenario
          ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
          : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
        <span>Có ít nhất 1 scenario đã kích hoạt</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        {hasSummary
          ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
          : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
        <span>Có thông tin summary</span>
      </div>
      {!isFirstNode && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {hasRequiredExp
            ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
            : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
          <span>Có EXP yêu cầu (&gt; 0)</span>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ padding: '24px' }}>
      <PageTitleSetter title={
        <Space size="small">
          <Typography.Link onClick={() => navigate(`/lifetrees/${lifeTreeId}`)}>
            {lifetreeDetail.title}
          </Typography.Link>
          <Text type="secondary">/</Text>
          <Text>{nodeDetail.title}</Text>
        </Space>
      } />

      {/* Page Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
        paddingBottom: 16,
        borderBottom: '1px solid #f0f0f0',
      }}>
        <Space align="center">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(`/lifetrees/${lifeTreeId}`)}
            style={{ padding: '0 4px' }}
          />
          <div>
            <Space align="center" size={8}>
              <Text strong style={{ fontSize: '18px' }}>{nodeDetail.title}</Text>
              <Tag color={isNodeActive ? 'green' : 'orange'} style={{ margin: 0 }}>
                {nodeDetail.status.toUpperCase()}
              </Tag>
              {refreshing && <Spin size="small" />}
            </Space>
            <div>
              <Typography.Link
                style={{ fontSize: '13px' }}
                onClick={() => navigate(`/lifetrees/${lifeTreeId}`)}
              >
                {lifetreeDetail.title}
              </Typography.Link>
            </div>
          </div>
        </Space>

        <Space>
          {!isNodeActive && (
            <Button
              type="primary"
              icon={<ThunderboltOutlined />}
              onClick={handleAutoDeployNode}
              loading={autoDeploying}
              style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
            >
              Tự động triển khai (Auto)
            </Button>
          )}
          {!isNodeActive && (
            <Popconfirm
              title="Kích hoạt node này?"
              description="Node sẽ chuyển sang active và người chơi có thể truy cập."
              onConfirm={handleActivateNode}
              okText="Kích hoạt"
              cancelText="Hủy"
              disabled={!canActivate}
            >
              <Tooltip title={activateTooltipContent} overlayStyle={{ maxWidth: 320 }}>
                <span style={{ display: 'inline-block', cursor: canActivate ? 'pointer' : 'not-allowed' }}>
                  <Button
                    icon={<PlayCircleOutlined />}
                    loading={activatingNode}
                    disabled={!canActivate}
                    style={canActivate ? { color: '#52c41a', borderColor: '#52c41a', pointerEvents: 'none' } : { pointerEvents: 'none' }}
                  >
                    Kích hoạt Node
                  </Button>
                </span>
              </Tooltip>
            </Popconfirm>
          )}
          {isNodeActive && (
            <Tag color="green" icon={<PlayCircleOutlined />} style={{ padding: '4px 10px', fontSize: '13px' }}>
              Đã kích hoạt
            </Tag>
          )}
        </Space>
      </div>

      {/* Auto Deployment Progress */}
      {autoDeploying && autoDeploymentProgress && (
        <Card 
          size="small" 
          style={{ 
            marginBottom: 16, 
            background: '#f9f0ff', 
            border: '1px solid #d3adf7',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}
        >
          <Row gutter={16} align="middle">
            <Col span={18}>
              <Space direction="vertical" style={{ width: '100%' }} size={0}>
                <Text strong style={{ color: '#531dab' }}>
                  Đang triển khai Node tự động (Bước {autoDeploymentProgress.step}/{autoDeploymentProgress.totalSteps})
                </Text>
                <Text type="secondary" style={{ fontSize: '13px' }}>
                  {autoDeploymentProgress.status}
                </Text>
                <Progress 
                  percent={Math.round((autoDeploymentProgress.step / autoDeploymentProgress.totalSteps) * 100)} 
                  size="small"
                  strokeColor="#722ed1"
                  showInfo={false}
                  style={{ marginTop: 8 }}
                />
              </Space>
            </Col>
            <Col span={6} style={{ textAlign: 'right' }}>
              <Space direction="vertical" align="end" size={0}>
                <Text strong style={{ color: '#531dab', fontSize: '18px' }}>
                  {Math.round((autoDeploymentProgress.step / autoDeploymentProgress.totalSteps) * 100)}%
                </Text>
                <Spin size="small" style={{ color: '#722ed1' }} />
              </Space>
            </Col>
          </Row>
          {autoDeploymentProgress.percentage > 0 && autoDeploymentProgress.percentage < 100 && (
            <div style={{ marginTop: 8 }}>
              <Progress 
                percent={autoDeploymentProgress.percentage} 
                size="small" 
                status="active"
                strokeColor="#b37feb"
                format={(percent) => <span style={{ fontSize: '10px' }}>{percent}% (Chi tiết bước)</span>}
              />
            </div>
          )}
        </Card>
      )}

      <Tabs
        activeKey={activeTab}
        onChange={handleTabChangeInUrl}
        type="card"
        items={[
          {
            key: '1',
            label: (
              <span>
                <FileTextOutlined />
                Thông tin cơ bản
              </span>
            ),
            children: (
              <NodeBasicInfoTab
                form={form}
                nodeDetail={nodeDetail}
                lifetreeDetail={lifetreeDetail}
                onFormChange={handleFormChange}
                onSave={handleSave}
                saving={saving}
                hasChanges={hasChanges || hasStateChanges}
                onGenerateSummary={handleGenerateSummary}
                generatingSummary={generatingSummary}
                onSetCoverImage={handleSetCoverImage}
                nodeImages={nodeImages}
                aiConcept={aiConcept}
                isGeneratingConcept={isGeneratingConcept}
                isGeneratingNodeImage={isGeneratingNodeImage}
                onGenerateNodeImageConcept={handleGenerateNodeImageConcept}
                onGenerateNodeImage={handleGenerateNodeImage}
              />
            )
          },
          {
            key: '2',
            label: (
              <span>
                <TeamOutlined />
                Nhân vật
              </span>
            ),
            children: (
              <NodeCharactersTab 
                nodeDetail={nodeDetail}
                lifetreeDetail={lifetreeDetail}
                mainCharacter={mainCharacter}
                mainCharacterStateInNode={mainCharacterStateInNode}
                npcRows={npcRows}
                onAddCharacterState={handleAddCharacterState}
                onRemoveCharacterState={handleRemoveCharacterState}
                onActivateState={handleActivateState}
                onShowCreateCharacterModal={() => setShowCreateCharacterModal(true)}
                onSetSelectedCharacterForState={setSelectedCharacterForState}
                onSetMethodSelectionModalOpen={setMethodSelectionModalOpen}
                onNPCCharacterChange={handleNPCCharacterChange}
                onNPCStateChange={handleNPCStateChange}
                onRemoveNPCRow={handleRemoveNPCRow}
                activatingStates={activatingStates}
                getAvailableNPCCharacters={getAvailableNPCCharacters}
                getAvailableStatesForCharacter={getAvailableStatesForCharacter}
                onPlanCharacters={handlePlanCharacters}
                planningCharacters={planningCharacters}
                onDeployCharacters={handleDeployCharacters}
                deployingCharacters={deployingCharacters}
                onDeployCharactersFromPlan={() => handleCreateCharactersFromPlan()}
                deployingCharactersFromPlan={creatingCharactersFromPlan}
                onDeployStatesFromPlan={() => handleCreateStatesFromPlan()}
                deployingStatesFromPlan={creatingStatesFromPlan}
                onDeployRelationships={() => handleDeployRelationships()}
                deployingRelationships={deployingRelationships}
                onDeployImages={() => handleDeployImages()}
                deployingImages={deployingImages}
                onResetCharacters={handleResetCharacters}
                resettingCharacters={resettingCharacters}
                deployingFull={deployingFull}
                deploymentProgress={deploymentProgress}
                direction={nodeDetail.direction}
                plan={nodeDetail.plan}
              />
            )
          },
          {
            key: '3',
            label: (
              <span>
                <PlayCircleOutlined />
                Scenario
              </span>
            ),
            children: (
              <NodeScenariosTab 
                nodeDetail={nodeDetail}
                lifetreeDetail={lifetreeDetail}
                onShowCreateScenarioModal={() => {
                  createScenarioForm.resetFields();
                  setSampleScenarioData(null);
                  setShowCreateScenarioModal(true);
                }}
                onShowSampleScenarioModal={() => {
                  sampleScenarioForm.resetFields();
                  setShowSampleScenarioModal(true);
                }}
                onProposeScenarios={handleProposeScenarios}
                proposingScenarios={proposingScenarios}
                onEditScenario={openEditScenarioModal}
                onViewScenarioImage={(scenario) => {
                  setSelectedScenarioForImage(scenario);
                  setGeneratedScenarioImage(null);
                  setShowScenarioImagePreviewModal(true);
                }}
                onCreateScenarioImage={openCreateScenarioImageModal}
                onQuickGenScenarioImage={handleQuickGenScenarioImage}
                onActivateScenario={handleActivateScenario}
                onDeleteScenario={handleDeleteScenario}
                onDeployScenarios={handleDeployScenarios}
                deployingScenarios={deployingScenarios}
                deploymentScenarioProgress={deploymentScenarioProgress}
                activatingScenarioIds={activatingScenarioIds}
                quickGenScenarioIds={quickGenScenarioIds}
                getCharacterNameById={getCharacterNameById}
              />
            )
          }
        ]}
      />

      {/* Create Character Modal */}
      <CreateCharacterModal
        visible={showCreateCharacterModal}
        onCancel={() => setShowCreateCharacterModal(false)}
        onSubmit={handleCreateCharacter}
        loading={creatingCharacter}
      />

      {/* Modal for AI Scenario Proposals */}
      <ScenarioProposalsModal
        open={showScenarioProposalsModal}
        onCancel={() => setShowScenarioProposalsModal(false)}
        scenarioProposals={scenarioProposals}
        onSelectProposal={handleSelectScenarioProposal}
        lifetreeDetail={lifetreeDetail}
        processingProposalIds={processingProposalIds}
        createdProposalIds={createdProposalIds}
      />

      {/* Create Scenario Sample Modal */}
      <SampleScenarioModal
        open={showSampleScenarioModal}
        onOk={handleGenerateSampleScenario}
        onCancel={() => {
          setShowSampleScenarioModal(false);
          sampleScenarioForm.resetFields();
        }}
        confirmLoading={generatingSample}
        form={sampleScenarioForm}
        characterStatesData={characterStatesData}
      />

      {/* Create Scenario Modal */}
      <ScenarioFormModal
        title={`Tạo Scenario${sampleScenarioData ? ' (Từ AI Sample)' : ''}`}
        open={showCreateScenarioModal}
        onOk={handleCreateScenario}
        onCancel={() => {
          setShowCreateScenarioModal(false);
          createScenarioForm.resetFields();
          setSampleScenarioData(null);
        }}
        confirmLoading={creatingScenario}
        form={createScenarioForm}
        okText="Tạo Scenario"
        isAISample={!!sampleScenarioData}
        lifetreeDetail={lifetreeDetail}
        availableNPCs={getAvailableNPCsForScenario()}
      />

      {/* Edit Scenario Modal */}
      <ScenarioFormModal
        title="Chỉnh sửa Scenario"
        open={showEditScenarioModal}
        onOk={handleUpdateScenario}
        onCancel={() => {
          setShowEditScenarioModal(false);
          editScenarioForm.resetFields();
          setEditingScenario(null);
        }}
        confirmLoading={updatingScenario}
        form={editScenarioForm}
        okText="Lưu thay đổi"
        lifetreeDetail={lifetreeDetail}
        availableNPCs={getAvailableNPCsForScenario()}
      />

      {/* Scenario Image Modal */}
      <ScenarioImagePreviewModal
        open={showScenarioImagePreviewModal}
        onCancel={() => {
          setShowScenarioImagePreviewModal(false);
          setSelectedScenarioForImage(null);
          setGeneratedScenarioImage(null);
        }}
        selectedScenario={selectedScenarioForImage}
        generatedImage={generatedScenarioImage}
        approving={approvingScenarioImage}
        onApprove={handleApproveScenarioImage}
      />

      {/* Character Plan Modal */}
      <CharacterPlanModal
        open={planModalOpen}
        onCancel={() => setPlanModalOpen(false)}
        characters={editablePlanCharacters}
        onCharacterChange={updatePlanCharacter}
        loading={formalizingCharacters}
        onConfirm={handleFormalizeCharacters}
      />

      {/* New Character State Creation Modals */}
      <MethodSelectionModal
        isOpen={methodSelectionModalOpen}
        onClose={() => {
          setMethodSelectionModalOpen(false);
          setSelectedCharacterForState(null);
        }}
        onSelectMethod={handleMethodSelection}
      />

      <AIDescriptionModal
        isOpen={aiDescriptionModalOpen}
        onClose={() => {
          setAIDescriptionModalOpen(false);
          setSelectedCharacterForState(null);
        }}
        onSubmit={handleAICompletion}
        isLoading={creatingState}
        characterName={getSelectedCharacterName()}
        availableStates={getCharacterStatesForAI()}
      />

      <CreateStateModal
        isOpen={createNewStateModalOpen}
        onClose={() => {
          setCreateNewStateModalOpen(false);
          setSelectedCharacterForState(null);
          setStateInitialData(undefined);
        }}
        onSubmit={handleCreateNewState}
        isLoading={creatingState}
        initialData={stateInitialData}
        characterName={getSelectedCharacterName()}
      />

      {/* Create Scenario Image Modal */}
      <CreateScenarioImageModal
        isOpen={showCreateScenarioImageModal}
        onClose={() => {
          setShowCreateScenarioImageModal(false);
          setSelectedScenarioForImage(null);
        }}
        onSubmit={handleCreateScenarioImageWithPrompt}
        onGeneratePrompt={handleGenerateScenarioPrompt}
        isLoading={creatingScenarioImage}
        generatePromptLoading={generatingScenarioPrompt}
        characters={(() => {
          if (!selectedScenarioForImage || !lifetreeDetail) return [];
          
          const involvedCharacters: { name: string; avatar: string | null; type: 'main' | 'npc' }[] = [];
          
          // 1. Add main character
          const mainCharRow = characterStatesData.find(row => row.characterType === 'main');
          if (mainCharRow) {
            involvedCharacters.push({
              name: mainCharRow.characterName,
              avatar: mainCharRow.characterAvatar,
              type: 'main'
            });
          } else if (mainCharacter) {
            // Fallback to basic character info if no state info in current node (unlikely)
            involvedCharacters.push({
              name: mainCharacter.name,
              avatar: mainCharacter.imageUrl,
              type: 'main'
            });
          }
          
          // 2. Add NPCs
          if (selectedScenarioForImage.npcIds) {
            selectedScenarioForImage.npcIds.forEach(npcId => {
              // Try to find in current node's character state data first (to get state avatar)
              const npcRow = characterStatesData.find(row => row.characterId === npcId);
              if (npcRow) {
                involvedCharacters.push({
                  name: npcRow.characterName,
                  avatar: npcRow.characterAvatar,
                  type: 'npc'
                });
              } else {
                // Fallback to basic character info from lifetree detail
                const npc = lifetreeDetail.characters.find(c => c.id === npcId);
                if (npc) {
                  involvedCharacters.push({
                    name: npc.name,
                    avatar: npc.imageUrl,
                    type: 'npc'
                  });
                }
              }
            });
          }
          
          return involvedCharacters;
        })()}
      />
    </div>
  );
};

export default NodeDetail;
